import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Target, Trophy, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function TeacherROIBanner() {
  const [roi, setRoi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/analytics/teacher-roi`, { headers: authHeader() })
      .then(r => setRoi(r.data))
      .catch(() => setRoi(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !roi?.enabled) return null;

  // No data state
  if (!roi.has_data) {
    return (
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="teacher-roi-banner-empty">
        <div className="flex items-center gap-3">
          <Sparkles className="text-blue-600 flex-shrink-0" size={20} />
          <div>
            <div className="text-sm font-semibold text-blue-900">ROI tracking ready</div>
            <div className="text-xs text-blue-700">{roi.message}</div>
          </div>
        </div>
      </div>
    );
  }

  const growthPositive = roi.avg_growth > 0;

  return (
    <div
      className="mb-6 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-5 shadow-sm"
      data-testid="teacher-roi-banner"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-shrink-0 w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
          <TrendingUp className="text-emerald-600" size={28} />
        </div>

        <div className="flex-1 min-w-[240px]">
          <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1">Your Impact This Period</div>
          <div className="text-xl font-bold text-gray-900 leading-tight" data-testid="roi-headline">
            {roi.callout_headline}
          </div>
          {roi.measurable_student_count > 0 && (
            <div className="text-xs text-gray-600 mt-1">
              Based on {roi.measurable_student_count} students with 30+ days between assessments · {roi.total_assessments} total assessments graded
            </div>
          )}
        </div>

        <div className="flex gap-3 flex-wrap">
          <div className="bg-white border border-emerald-200 rounded-lg px-4 py-3 min-w-[110px]" data-testid="roi-avg-growth">
            <div className="text-xs text-gray-500 uppercase">Avg Growth</div>
            <div className={`text-2xl font-bold ${growthPositive ? 'text-emerald-600' : 'text-gray-500'}`}>
              {growthPositive ? '+' : ''}{roi.avg_growth.toFixed(2)}
            </div>
            <div className="text-[10px] text-gray-400">WIDA pts</div>
          </div>
          <div className="bg-white border border-blue-200 rounded-lg px-4 py-3 min-w-[110px]" data-testid="roi-on-track">
            <div className="text-xs text-gray-500 uppercase flex items-center gap-1"><Target size={10}/> On Track</div>
            <div className="text-2xl font-bold text-blue-600">{roi.on_track_pct}%</div>
            <div className="text-[10px] text-gray-400">{roi.on_track_count} students</div>
          </div>
          <div className="bg-white border border-amber-200 rounded-lg px-4 py-3 min-w-[110px]" data-testid="roi-class-avg">
            <div className="text-xs text-gray-500 uppercase">Current Avg</div>
            <div className="text-2xl font-bold text-amber-600">{roi.current_class_avg_wida.toFixed(2)}</div>
            <div className="text-[10px] text-gray-400">Proficiency level</div>
          </div>
        </div>
      </div>

      {/* Top movers */}
      {roi.top_movers && roi.top_movers.length > 0 && (
        <div className="mt-4 pt-4 border-t border-emerald-100">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-2">
            <Trophy size={12}/> Top Movers
          </div>
          <div className="flex gap-2 flex-wrap">
            {roi.top_movers.map((m, i) => (
              <div key={m.student_id} className="bg-white border border-emerald-200 rounded-full px-3 py-1 text-xs flex items-center gap-2" data-testid={`top-mover-${i}`}>
                <span className="font-semibold text-gray-800">{m.student_name}</span>
                <span className="text-emerald-600 font-bold">+{m.delta.toFixed(2)}</span>
                <span className="text-gray-400">({m.baseline.toFixed(1)}→{m.current.toFixed(1)})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
